import React, { Fragment } from 'react';
import * as HeadlessUI from '@headlessui/react';
import { FaCheckCircle, FaTimesCircle, FaInfoCircle, FaTimes } from 'react-icons/fa';

const SyncResultsModal = ({ isOpen, onClose, results }) => {
  if (!results) return null;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'updated':
        return <FaCheckCircle className="text-green-500" />;
      case 'failed':
        return <FaTimesCircle className="text-red-500" />;
      case 'skipped':
        return <FaInfoCircle className="text-gray-500" />;
      default:
        return null;
    }
  };

  const allSkipped = results.totalSkipped === results.totalProcessed && results.totalProcessed > 0;

  return (
    <HeadlessUI.Transition appear show={isOpen} as={Fragment}>
      <HeadlessUI.Dialog onClose={onClose} className="relative z-50">
        <HeadlessUI.TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        </HeadlessUI.TransitionChild>

        <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
          <HeadlessUI.TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <HeadlessUI.DialogPanel className="inline-block w-full max-w-2xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
              <div className="border-b border-gray-200 pb-4 mb-4 flex justify-between items-center">
                <HeadlessUI.DialogTitle as="h3" className="text-lg font-semibold text-gray-900">
                  Sync Results
                </HeadlessUI.DialogTitle>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-500 focus:outline-none"
                >
                  <FaTimes />
                </button>
              </div>

              <div className="space-y-4">
                {allSkipped ? (
                  <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg">
                    <FaInfoCircle className="text-gray-500 mr-3" />
                    <p className="text-gray-700">All invoices are up to date with Xero.</p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="bg-green-50 p-3 rounded-lg">
                        <p className="text-green-700">Updated: {typeof results.totalUpdated === 'object' && results.totalUpdated !== null ? JSON.stringify(results.totalUpdated) : results.totalUpdated}</p>
                      </div>
                      <div className="bg-red-50 p-3 rounded-lg">
                        <p className="text-red-700">Failed: {typeof results.totalFailed === 'object' && results.totalFailed !== null ? JSON.stringify(results.totalFailed) : results.totalFailed}</p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-gray-700">Skipped: {typeof results.totalSkipped === 'object' && results.totalSkipped !== null ? JSON.stringify(results.totalSkipped) : results.totalSkipped}</p>
                      </div>
                    </div>

                    {results.details?.length > 0 && (
                      <div className="max-h-96 overflow-y-auto border rounded-lg">
                        {results.details.map((detail, index) => (
                          <div
                            key={`${detail.invoiceId ?? 'row'}-${index}`}
                            className={`p-4 flex items-start gap-3 ${
                              index !== results.details.length - 1 ? 'border-b' : ''
                            }`}
                          >
                            <div className="flex-shrink-0 mt-1">
                              {getStatusIcon(detail.status)}
                            </div>
                            <div className="flex-grow">
                              <p className="font-medium">Invoice ID: {detail.invoiceId}</p>
                              <p className="text-sm text-gray-600">
                                {typeof detail.message === 'object' && detail.message !== null ? JSON.stringify(detail.message) : detail.message}
                              </p>
                              {detail.updates && (
                                <div className="mt-2 text-sm">
                                  <p className="text-gray-700">Updates:</p>
                                  <ul className="list-disc list-inside pl-4">
                                    {Object.entries(detail.updates).map(([key, value]) => (
                                      <li key={key} className="text-gray-600">
                                        {key.replace(/_/g, ' ')}: {typeof value === 'object' && value !== null ? JSON.stringify(value) : value}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end">
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Close
                </button>
              </div>
            </HeadlessUI.DialogPanel>
          </HeadlessUI.TransitionChild>
        </div>
      </HeadlessUI.Dialog>
    </HeadlessUI.Transition>
  );
};

export default SyncResultsModal;